/**
 * Cart optimistic updates — instant totals on +/- and trash clicks.
 *
 * Listens via multiple fallback paths for maximum reliability:
 *   1. quantity-selector:update theme event (primary)
 *   2. Direct click on +/- buttons inside .quantity-selector
 *   3. input/change events on the quantity input
 *
 * On any of those, we paint the new line total, cart total, and checkout
 * button amount immediately. The existing cart-items-component still fires
 * the server request 300ms later; when the server responds, morphSection
 * overwrites our optimistic values with authoritative ones.
 *
 * Set window.__fpCartOptimisticDebug = true in devtools to log activity.
 */
(() => {
  if (window.__fpCartOptimistic) return;
  window.__fpCartOptimistic = true;

  const DEBUG = () => window.__fpCartOptimisticDebug === true;
  const log = (...args) => { if (DEBUG()) console.log('[fp-cart-optimistic]', ...args); };

  // --- Money helpers -------------------------------------------------------

  function parseMoney(text) {
    if (!text) return 0;
    const cleaned = String(text).replace(/[^\d.-]/g, '');
    const n = parseFloat(cleaned);
    return isFinite(n) ? n : 0;
  }

  let cachedFormatter = null;

  function getMoneyFormatter() {
    if (cachedFormatter) return cachedFormatter;

    // Try cart total first, then any line price in DOM, to infer format
    const refEl =
      document.querySelector('.cart-totals__total-value') ||
      document.querySelector('[data-cart-subtotal]') ||
      document.querySelector('.cart-items__price text-component');

    const reference = refEl
      ? (refEl.textContent || refEl.getAttribute('value') || '')
      : '';

    const match = reference.match(/^(\D*)([\d,.]+)(\D*)$/);
    if (!match) {
      cachedFormatter = (amount) => '\u20B9' + Math.round(amount).toLocaleString('en-IN');
      return cachedFormatter;
    }
    const [, prefix, number, suffix] = match;
    const hasDecimals = number.includes('.');
    const decimalCount = hasDecimals ? (number.split('.')[1] || '').length : 0;

    cachedFormatter = (amount) =>
      prefix +
      amount.toLocaleString('en-IN', {
        minimumFractionDigits: decimalCount,
        maximumFractionDigits: decimalCount,
      }) +
      suffix;
    return cachedFormatter;
  }

  const formatMoney = (amount) => getMoneyFormatter()(amount);

  // --- DOM helpers ---------------------------------------------------------

  function getCartRows() {
    // Fallback chain: explicit data-product-id (our attribute), then any cart row
    let rows = document.querySelectorAll('.cart-items__table-row[data-product-id]');
    if (!rows.length) {
      rows = document.querySelectorAll('.cart-items__table-row[data-key]');
    }
    return Array.from(rows);
  }

  function readLineTotal(row) {
    const el = row.querySelector('.cart-items__price text-component')
      || row.querySelector('.cart-items__price');
    return el ? parseMoney(el.textContent) : 0;
  }

  function readQuantity(row) {
    const input = row.querySelector('.cart-items__quantity input')
      || row.querySelector('input[name="updates[]"]')
      || row.querySelector('input[data-cart-quantity]');
    if (!input) return 1;
    const n = parseInt(input.value, 10);
    return isFinite(n) && n > 0 ? n : 1;
  }

  function writeText(el, value) {
    if (!el) return;
    // Handle text-component specially: keep `value` attr in sync so shimmer
    // pseudo-element shows the same string.
    if (el.tagName && el.tagName.toLowerCase() === 'text-component') {
      el.setAttribute('value', value);
    }
    el.textContent = value;
  }

  function paintRowLineTotal(row, newLineTotal) {
    const formatted = formatMoney(newLineTotal);
    const tc = row.querySelector('.cart-items__price text-component');
    if (tc) writeText(tc, formatted);
    else {
      const plain = row.querySelector('.cart-items__price');
      if (plain) writeText(plain, formatted);
    }
  }

  function sumCartTotal(excludeRow) {
    // Skip bundle child rows (data-parent-key) to avoid double-counting
    // when a bundle app assigns per-child prices alongside a parent total.
    // Today Shopify's convention is parent=total/children=0, so this is a
    // defensive no-op on current carts — safeguards future bundle setups.
    return getCartRows()
      .filter((r) => r !== excludeRow && !r.getAttribute('data-parent-key'))
      .reduce((sum, r) => sum + readLineTotal(r), 0);
  }

  function paintCartTotal(newTotal) {
    const formatted = formatMoney(newTotal);
    // Any text-component displaying the total
    document.querySelectorAll(
      '.cart-totals__total-value, [data-cart-subtotal]'
    ).forEach((el) => {
      if (el.tagName && el.tagName.toLowerCase() === 'text-component') {
        writeText(el, formatted);
      } else {
        const inner = el.querySelector('text-component');
        if (inner) writeText(inner, formatted);
        else writeText(el, formatted);
      }
    });
  }

  function paintCheckoutButton(newTotal) {
    const formatted = formatMoney(newTotal);
    // Drawer CTA — the GoKwik <button> and the native <a> both carry
    // .fp2-cta .fp-place-order with the same .btn-text > span:first-child slot
    // (sections/fp-cart-drawer.liquid), so one selector covers both switch states.
    document.querySelectorAll('.fp2-cta .fp-place-order .btn-text > span:first-child')
      .forEach((el) => { el.textContent = `Place order \u00B7 ${formatted}`; });
    // /cart page, GoKwik branch (uppercase copy, snippets/cart-summary.liquid).
    document.querySelectorAll('.gokwik-checkout:not(.fp2-cta) button.fp-place-order .btn-text > span:first-child')
      .forEach((el) => { el.textContent = `PLACE ORDER \u00B7 ${formatted}`; });
    // /cart page, native branch.
    document.querySelectorAll('.cart__checkout-button .button-text')
      .forEach((el) => { el.textContent = `Check out \u00B7 ${formatted}`; });
  }

  // Paint the free-shipping progress bar + remaining-amount text. Threshold
  // mirrors cart-products.liquid's 199900 cents (₹1,999); since 2026-06-13
  // the bar is two-milestone (₹999 COD tick + ₹1,999 FREE) — the remaining
  // amount counts toward whichever milestone the server rendered
  // (data-next-milestone). State/copy transitions are left to the server
  // morph — crossing a milestone is rare enough that ~500ms is acceptable.
  var FREE_SHIPPING_THRESHOLD_RUPEES = 1999;
  function paintFreeShippingBar(newTotalRupees) {
    // v2 slide cart (2026-07-12): in the DRAWER the rewards ladder renders in
    // a fixed slot under the header — OUTSIDE .cart-items__wrapper — while the
    // /cart page keeps it inline. Query unscoped: only one bar exists per page
    // (the drawer block is not rendered on /cart at all).
    var wrap = document.querySelector('.fp-freeship-wrapper');
    var bar = document.querySelector('.fp-freeship-progress-fill');
    var remainingEl = document.querySelector('.fp-freeship-remaining');
    if (!bar) return;

    var totalRupees = Math.max(0, Math.round(newTotalRupees));
    var nextMilestone = (wrap && parseInt(wrap.getAttribute('data-next-milestone'), 10)) || FREE_SHIPPING_THRESHOLD_RUPEES;
    var remaining = Math.max(0, nextMilestone - totalRupees);
    // Since 2026-07-10 the bar is a two-rung rewards track (fp-cart-rewards):
    // the fill is proportional to the FULL track (data-scale-max, e.g. ₹4,999),
    // not the next milestone — otherwise the fill overshoots between morphs.
    var scaleMax = (wrap && parseInt(wrap.getAttribute('data-scale-max'), 10)) || FREE_SHIPPING_THRESHOLD_RUPEES;
    var progress = Math.min(100, Math.floor(totalRupees * 100 / scaleMax));

    bar.style.width = progress + '%';
    if (remainingEl && remaining > 0) {
      remainingEl.textContent = formatMoney(remaining);
    }
  }

  // --- Core update functions ----------------------------------------------

  // Rapid concurrent click mitigation: full fix (request versioning with
  // server support) is out of scope. The existing mitigation chain is:
  //   1. cart-items-component.js adds `cart-items-disabled` class with
  //      pointer-events: none during fetches, blocking rapid clicks at
  //      the UI layer.
  //   2. The quantity change handler is debounced by ~300ms.
  //   3. Our optimistic paints always read the latest DOM state (no stale
  //      cache), so even if server responses arrive out of order, the
  //      visible value reflects the authoritative count.
  // Together these reduce the out-of-order response window to a narrow
  // edge case. Tracked here so future maintainers understand the tradeoff.
  function optimisticQuantityUpdate(row, newQuantity) {
    if (!row) return;

    // Prefer the authoritative unit price from the data-unit-price attribute
    // (rendered server-side from item.final_price in cents). This avoids a
    // race: by the time this handler runs, input.value is already the NEW
    // quantity, but the line total in the DOM still reflects the OLD quantity
    // — so dividing line total by current qty would give the wrong unit price.
    const unitPriceCentsRaw = row.dataset.unitPrice;
    let unitPrice = 0;

    if (unitPriceCentsRaw) {
      const cents = parseInt(unitPriceCentsRaw, 10);
      if (isFinite(cents) && cents > 0) unitPrice = cents / 100;
    }

    if (!unitPrice) {
      // Fallback: if data-unit-price is missing (e.g. older cached markup),
      // use line-total ÷ a stable qty. We can't trust input.value here, so
      // read the old qty from a stashed attribute if we have one.
      const currentLineTotal = readLineTotal(row);
      const stashedQty = parseInt(row.dataset.fpLastQty, 10);
      const fallbackQty = isFinite(stashedQty) && stashedQty > 0 ? stashedQty : readQuantity(row);
      if (fallbackQty > 0 && currentLineTotal > 0) {
        unitPrice = currentLineTotal / fallbackQty;
      }
    }

    if (!unitPrice) {
      log('skip: no unit price available');
      return;
    }

    const newLineTotal = unitPrice * newQuantity;

    paintRowLineTotal(row, newLineTotal);
    // Stash the quantity we just painted so the next interaction can use it
    // as a fallback even if data-unit-price is missing.
    row.dataset.fpLastQty = String(newQuantity);

    const cartTotal = sumCartTotal();
    paintCartTotal(cartTotal);
    paintCheckoutButton(cartTotal);
    paintFreeShippingBar(cartTotal);

    log('qty update', { newQuantity, unitPrice, newLineTotal, cartTotal });
  }

  // Robustly set the header cart-icon bubble count. Tries the component's
  // own renderCartBubble method first, then falls back to direct DOM
  // manipulation of the bubble elements. Also persists to sessionStorage
  // so cart-icon's ensureCartBubbleIsCorrect hook can't resurrect stale
  // values on navigation.
  function setCartBubbleCount(count) {
    const cartIcon = /** @type {any} */ (document.querySelector('cart-icon'));
    if (!cartIcon) return;

    // Primary path: component method
    if (typeof cartIcon.renderCartBubble === 'function') {
      try { cartIcon.renderCartBubble(count, false); } catch (_) { /* fall through */ }
    }

    // Fallback path: direct DOM manipulation (defensive — in case the
    // component method fails or is partially applied).
    const bubbleCount =
      cartIcon.querySelector('[ref="cartBubbleCount"]') ||
      cartIcon.querySelector('.cart-bubble__text-count');
    const bubble =
      cartIcon.querySelector('[ref="cartBubble"]') ||
      cartIcon.querySelector('.cart-bubble');

    if (bubbleCount) {
      bubbleCount.textContent = count > 0 && count < 100 ? String(count) : '';
      bubbleCount.classList.toggle('hidden', count === 0);
    }
    if (bubble) {
      bubble.classList.toggle('visually-hidden', count === 0);
    }
    cartIcon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);

    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({ value: String(count), timestamp: Date.now() })
      );
    } catch (_) { /* no-op */ }
  }

  function optimisticRowRemove(row) {
    if (!row) return;
    const cartTotal = sumCartTotal(row);
    paintCartTotal(cartTotal);
    paintCheckoutButton(cartTotal);
    paintFreeShippingBar(cartTotal);

    // Immediate optimistic decrement — the row is still in the DOM at this
    // point (capture-phase click), so we exclude it from the count.
    const remainingNow = getCartRows().filter((r) => r !== row).length;
    setCartBubbleCount(remainingNow);

    // Horizon's onLineItemRemove plays a CSS removal animation before the
    // row.remove() actually runs — so `getCartRows()` still returns the
    // removing row for the animation duration (~300ms). The reconciliation
    // timers must exclude `row` (both directly, and via the `.removing`
    // class Horizon adds) so the bubble doesn't rebound to the pre-removal
    // count while the animation plays.
    const countExcludingRemoving = () =>
      getCartRows().filter((r) => r !== row && !r.classList.contains('removing')).length;
    setTimeout(() => setCartBubbleCount(countExcludingRemoving()), 120);
    setTimeout(() => setCartBubbleCount(countExcludingRemoving()), 700);

    log('row remove', { cartTotal, remainingNow });
  }

  // --- Event wiring (multiple paths for reliability) ----------------------

  // Path 1: Horizon theme event. Bubbles from quantity-selector components.
  document.addEventListener('quantity-selector:update', (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!target || !target.closest) return;
    if (!target.closest('cart-items-component')) return;

    const detail = /** @type {any} */ (event).detail;
    if (!detail) return;
    const { quantity, cartLine } = detail;
    if (typeof cartLine !== 'number') return;

    const row = getCartRows()[cartLine - 1];
    log('theme event', { quantity, cartLine, foundRow: !!row });
    if (!row) return;

    if (typeof quantity === 'number' && quantity > 0) {
      optimisticQuantityUpdate(row, quantity);
    } else {
      optimisticRowRemove(row);
    }
  });

  // Path 2: Click fallback — +/- buttons inside cart quantity selectors.
  // Capture phase so we run before the component's own handlers even if
  // they stop propagation at target phase.
  document.addEventListener('click', (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!target || !target.closest) return;

    // Trash icon?
    const removeBtn = target.closest('.cart-items__remove');
    if (removeBtn) {
      const row = removeBtn.closest('.cart-items__table-row');
      if (row) {
        log('trash click');
        optimisticRowRemove(row);
      }
      return;
    }

    // +/- button inside a cart quantity selector?
    const qtyButton = target.closest('.quantity-selector button');
    if (!qtyButton) return;
    const row = qtyButton.closest('.cart-items__table-row');
    if (!row) return; // not in the cart

    // Native handler will update the input value synchronously via
    // updateQuantity → input.value = newValue. Wait a microtask so we
    // read the updated value, then paint.
    queueMicrotask(() => {
      const input = row.querySelector('.cart-items__quantity input');
      if (!input) return;
      const newQty = parseInt(input.value, 10);
      if (!isFinite(newQty)) return;
      log('click fallback', { newQty });
      if (newQty > 0) optimisticQuantityUpdate(row, newQty);
      else optimisticRowRemove(row);
    });
  }, true);

  // Path 3: input/change events — covers typing directly into the quantity
  // input. The + / - buttons set input.value programmatically, which doesn't
  // fire 'input', so this only catches user typing.
  document.addEventListener('change', (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!target || !(target instanceof HTMLInputElement)) return;
    const row = target.closest('.cart-items__table-row');
    if (!row) return;
    const newQty = parseInt(target.value, 10);
    if (!isFinite(newQty)) return;
    log('change event', { newQty });
    if (newQty > 0) optimisticQuantityUpdate(row, newQty);
    else optimisticRowRemove(row);
  }, true);

  // Reset format cache on cart update (in case currency or locale flips)
  document.addEventListener('cart:update', () => {
    cachedFormatter = null;
  });

  // Also reset the cache on pageshow. Fires on bfcache hydration (back/
  // forward navigation) and on regular loads. If the user switched
  // locale/currency in another tab and returns here via the back button,
  // the cached formatter would otherwise keep rendering in the old locale
  // until the next cart mutation.
  window.addEventListener('pageshow', () => {
    cachedFormatter = null;
  });

  // Suppress auto cart-drawer open when ATC errored. product-form.js already
  // surfaces the error inline on the PDP (addToCartTextError), so letting the
  // drawer open alongside would produce a confusing "error + open drawer"
  // double signal. Capture phase runs before cart-drawer/cart-items/cart-icon
  // document-level listeners, halting propagation when didError is set.
  document.addEventListener('cart:update', function (event) {
    if (event && event.detail && event.detail.data && event.detail.data.didError === true) {
      event.stopImmediatePropagation();
    }
  }, true);

  // Authoritative bubble reconciliation. Many code paths fire cart:update
  // with varying shapes (resource with items, itemCount-only, nothing at
  // all). If any single path misses the bubble update — classic symptom:
  // bubble stuck at 1 after clearing to empty — this safety net catches
  // it by fetching /cart.js (the source of truth) ~250ms after the update
  // settles and setting the bubble directly.
  //
  // Debounced so rapid changes (qty +/- spam) only trigger one fetch.
  var bubbleSyncTimer = null;
  function scheduleBubbleSync() {
    if (bubbleSyncTimer) clearTimeout(bubbleSyncTimer);
    bubbleSyncTimer = setTimeout(function () {
      bubbleSyncTimer = null;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          if (cart && Array.isArray(cart.items)) {
            setCartBubbleCount(cart.items.length);
          }
        })
        .catch(function () { /* silent fail — optimistic paints already landed */ });
    }, 250);
  }
  document.addEventListener('cart:update', scheduleBubbleSync);

  // ===== GoKwik checkout button — immediate re-enable after cart morph =====
  // Bug fix 2026-04-27: when the cart drawer morphs (qty change, item add/
  // remove), the place-order button is re-rendered with `disabled` attribute
  // from cart-summary.liquid markup. The body-wide MutationObserver in
  // gokwik.liquid takes ~700ms (debounced) to re-enable it. During that
  // window, browsers suppress click events on disabled buttons — users see
  // a "frozen" button. If they click again, the click MAY hit while
  // onCheckoutClick isn't yet engaged with the new DOM, falling through to
  // native /checkout via the onCheckoutNow handler.
  //
  // Fix: listen for cart:update directly. When fired (after morphSection),
  // re-enable GoKwik buttons on the next microtask. Skips the 700ms debounce.
  // Only fires after fp-gokwik-ready is set, so we don't enable a not-yet-
  // functional button on initial page load.
  document.addEventListener('cart:update', function () {
    if (!document.body.classList.contains('fp-gokwik-ready')) return;
    queueMicrotask(function () {
      document.querySelectorAll('.gokwik-checkout button.fp-place-order, #gokwik-buy-now').forEach(function (btn) {
        btn.disabled = false;
        btn.classList.remove('disabled');
      });
    });
  });

  // ===== GoKwik readiness tracking =====
  // While GoKwik's external script is still initializing, we let its native
  // loader spinner show (via its own CSS on .disabled). Users get feedback
  // that the button is waking up. Once gokwikLoaded fires, we set a body
  // class that (a) hides the spinner and (b) keeps the button visually
  // enabled (opacity: 1) through subsequent morph → debounce cycles.
  //
  // Sync check absorbs the "already fired" race: if GoKwik's own handler
  // has already stripped the disabled attribute (from snippets/gokwik.liquid
  // line 70-80), we know GoKwik is ready before our listener could attach.
  //
  // Safety fallback: if gokwikLoaded doesn't fire within 8s (GoKwik down,
  // script blocked, network issue), flip the class anyway and force-enable
  // the Place Order buttons. Users fall through to native Shopify checkout
  // via the onCheckoutNow handler's existing fallback. Timer is cleared
  // when the event arrives to avoid wasted cycles.
  //
  // 2026-04-27: bumped from 5s → 8s. On 3G/slow-4G, the GoKwik bundle
  // (~165KB) takes 4-7s to parse + execute. The previous 5s fallback was
  // firing for slow-network users mid-load, enabling the button visually
  // but before onCheckoutClick was registered globally — so clicks fell
  // through to native Shopify checkout instead of opening the GoKwik flow.
  function isGokwikAlreadyReady() {
    var btn = document.querySelector('.gokwik-checkout button.fp-place-order');
    return btn && !btn.hasAttribute('disabled');
  }

  if (!document.body.classList.contains('fp-gokwik-ready')) {
    if (isGokwikAlreadyReady()) {
      document.body.classList.add('fp-gokwik-ready');
    } else {
      var fallbackTimer = setTimeout(function () {
        if (document.body.classList.contains('fp-gokwik-ready')) return;
        document.body.classList.add('fp-gokwik-ready');
        // 2026-09-10: this timer tripping means gokwikLoaded never fired —
        // GoKwik is down, blocked, or too slow. It force-enables the button
        // and the shopper then falls through to native /checkout. That was
        // completely silent until now, which is why nobody noticed GoKwik
        // failing far more often on Maker's alt pages than on our own.
        // Gated on gokwik mode: in native mode gokwikLoaded never fires by
        // design, so an ungated beacon would be pure noise.
        if (window.FP_CHECKOUT_MODE !== 'native' && window.fpCheckoutFallbackBeacon) {
          window.fpCheckoutFallbackBeacon('gokwik-load-timeout');
        }
        document.querySelectorAll('.gokwik-checkout button.fp-place-order').forEach(function (btn) {
          btn.disabled = false;
          btn.classList.remove('disabled');
        });
      }, 8000);

      window.addEventListener('gokwikLoaded', function () {
        document.body.classList.add('fp-gokwik-ready');
        clearTimeout(fallbackTimer);
      }, { once: true });
    }
  }

  // Inject scoped CSS once, rather than requiring a stylesheet edit.
  //
  // Gating strategy:
  // - Before fp-gokwik-ready: let GoKwik's own .disabled styles apply —
  //   spinner visible, button greyed. This gives users feedback during
  //   the ~500-1500ms GoKwik boot on a fresh page load.
  // - After fp-gokwik-ready: hide the spinner permanently, force opacity
  //   to 1 so brief re-disabled states (during cart morph / 700ms debounce)
  //   don't produce visible flicker. The native `disabled` attribute
  //   still blocks clicks during those narrow windows — users can't
  //   submit to GoKwik before it's rebonded.
  // - During fp-cart-post-update: draw a 2px progress bar on the button
  //   to signal "syncing" after any cart change.
  const style = document.createElement('style');
  style.textContent = `
    body.fp-gokwik-ready .gokwik-checkout button.fp-place-order .addloadr#btn-loader {
      display: none !important;
    }
    body.fp-gokwik-ready .gokwik-checkout button.fp-place-order {
      opacity: 1 !important;
    }
    body.fp-cart-post-update .fp2-cta .fp-place-order::before,
    body.fp-cart-post-update .gokwik-checkout button.fp-place-order::before {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.6) 50%,
        transparent 100%
      );
      background-size: 55% 100%;
      background-repeat: no-repeat;
      background-position: -55% 0;
      animation: fp-btn-progress 0.95s linear infinite;
      pointer-events: none;
      z-index: 1;
    }
    @keyframes fp-btn-progress {
      from { background-position: -55% 0; }
      to   { background-position: 155% 0; }
    }
  `;
  document.head.appendChild(style);

  let postUpdateTimer = null;

  function stripCartShimmer() {
    document
      .querySelectorAll('.cart-items__price text-component[shimmer], text-component[shimmer]')
      .forEach((el) => el.removeAttribute('shimmer'));
  }

  document.addEventListener('cart:update', () => {
    document.body.classList.add('fp-cart-post-update');
    if (postUpdateTimer) clearTimeout(postUpdateTimer);
    postUpdateTimer = setTimeout(() => {
      document.body.classList.remove('fp-cart-post-update');
      postUpdateTimer = null;
    }, 900); // a bit more than GoKwik's 700ms debounce

    // Strip any [shimmer] attrs the morph reapplied to cart text-components
    requestAnimationFrame(stripCartShimmer);
    setTimeout(stripCartShimmer, 50);
  });

  // Intercept shimmer() calls on cart text-components: as soon as the
  // attribute is added, strip it. Keeps the optimistic value visible
  // continuously through the server round-trip.
  const shimmerObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type !== 'attributes' || m.attributeName !== 'shimmer') continue;
      const target = /** @type {Element} */ (m.target);
      if (!target.hasAttribute('shimmer')) continue;
      // Only strip for cart-related text-components, not other uses.
      // Broad ancestor selectors (cart-items-component, .cart-drawer) catch
      // any cart text-component even if Horizon moves the specific inner
      // selectors around in a future update.
      if (
        target.closest('cart-items-component') ||
        target.closest('.cart-drawer') ||
        target.closest('.cart-items__price') ||
        target.closest('.cart-totals') ||
        target.hasAttribute('data-cart-subtotal') ||
        target.classList.contains('cart-totals__total-value')
      ) {
        target.removeAttribute('shimmer');
      }
    }
  });
  shimmerObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['shimmer'],
    subtree: true,
  });

  log('installed; listening on document for quantity-selector:update + click + change');
})();
